import React, { useState } from "react";
import announcementsHero from "@/assets/announcements-hero.webp";
import ProductPageLayout from "@/components/ProductPageLayout";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { motion } from "framer-motion";
import { 
  Megaphone, 
  Bell, 
  Eye, 
  Settings, 
  Users, 
  CheckCircle, 
  ArrowRight,
  TrendingUp,
  Target,
  Clock,
  Star,
  Shield,
  Calendar,
  Zap,
  Globe
} from "lucide-react";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import TrustLogosSection from "@/components/trust/TrustLogosSection";
import HeroBadgesSection from "@/components/hero/HeroBadgesSection";

const Announcements = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const features = [
    {
      icon: <Megaphone className="h-6 w-6" />,
      title: "Company-Wide Reach",
      description: "Deliver messages across all channels instantly",
      features: ["Feed integration", "Email delivery", "Mobile notifications", "Slack integration"],
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Eye className="h-6 w-6" />,
      title: "Read Tracking",
      description: "Know exactly who's seen your announcements",
      features: ["Mark as seen", "View analytics", "Reminder notifications", "Engagement insights"],
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Calendar className="h-6 w-6" />,
      title: "Smart Scheduling",
      description: "Schedule announcements for perfect timing",
      features: ["Future scheduling", "Time zones", "Auto-publish", "Duration control"],
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Targeted Delivery",
      description: "Send to specific teams or departments",
      features: ["Department filters", "Role-based", "Location targeting", "Custom groups"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: <Settings className="h-6 w-6" />,
      title: "Flexible Permissions",
      description: "Control who can create announcements",
      features: ["Admin controls", "Manager access", "Custom roles", "Approval workflows"],
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: <Bell className="h-6 w-6" />,
      title: "Priority Alerts",
      description: "Mark urgent announcements for immediate attention",
      features: ["Urgent flagging", "Push notifications", "High visibility", "Instant delivery"],
      color: "from-indigo-500 to-blue-500"
    }
  ];

  const benefits = [
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "94% Read Rate",
      description: "Critical announcements achieve significantly higher visibility than traditional email"
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "3x Faster Delivery",
      description: "Multi-channel approach ensures urgent information reaches employees immediately"
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "89% Manager Adoption",
      description: "Leaders actively use announcements for team and department communication"
    }
  ];

  return (
    <ProductPageLayout
      title="Company Announcements Software - Internal Communication Platform"
      description="Deliver important company messages with Assembly's Announcements platform. Track who's seen them, schedule for perfect timing, and reach employees across all channels."
      keywords="company announcements, internal communications, employee messaging, organizational updates, corporate communications"
      ctaTitle="Ready to Transform Your Internal Communications?"
      ctaDescription="See how Assembly's Announcements platform can ensure important messages reach every employee."
      ctaButtonText="Get a Demo"
      ctaVariant="blue"
      onCtaClick={() => setIsDemoModalOpen(true)}
    >
      {/* Full Height Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50/30 font-sans overflow-hidden min-h-screen">
        {/* Subtle Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/8 via-purple-500/5 to-blue-400/4 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-purple-400/6 via-blue-500/5 to-slate-400/3 rounded-full blur-3xl animate-pulse" style={{
            animationDelay: '1s'
          }}></div>
        </div>
        
        {/* Dot Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.15]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(59, 130, 246, 0.3) 2px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}></div>
        </div>

        <section className="relative flex items-center justify-center py-8 lg:py-10 px-4 lg:px-8 min-h-screen pt-20 lg:pt-24">
          <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px] relative px-6 lg:px-6 xl:px-8 pb-3 lg:pb-4">
            <div className="flex flex-col lg:grid lg:grid-cols-7 gap-6 lg:gap-4 items-start">
              {/* Left Section: Text Content */}
              <div className="flex flex-col items-start text-left lg:col-span-4 h-full justify-center space-y-4 lg:space-y-6">
                {/* Pills */}
                <motion.div 
                  className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 mt-6 lg:mt-8 mb-3 lg:mb-4"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                >
                  <div className="relative overflow-hidden px-3 py-1.5 rounded-full bg-black/5 border border-black/10 backdrop-blur-sm hover:bg-black/10 transition-all duration-300 hover:-translate-y-0.5">
                    <h1 className="text-xs font-normal text-gray-900 tracking-wide uppercase">Company Announcements Platform</h1>
                  </div>
                </motion.div>

                {/* Main Heading */}
                <div className="mb-4 lg:mb-6">
                  <motion.h2 
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl font-bold text-black tracking-tight font-sans"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    Announcements that actually get read
                  </motion.h2>
                </div>

                {/* Subheading */}
                <motion.p 
                  className="text-sm sm:text-base lg:text-base xl:text-lg text-gray-600 mb-6 lg:mb-8 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  Assembly's Announcements platform delivers important company messages with confidence. Track who's seen them, schedule for perfect timing, and reach employees across all channels with read tracking and smart targeting.
                </motion.p>
                
                {/* Action Buttons */}
                <motion.div 
                  className="mb-4 lg:mb-6 w-full flex flex-col sm:flex-row gap-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                >
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md transition-all duration-200" 
                    onClick={() => setIsDemoModalOpen(true)}
                  >
                    Book a Demo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
                
                {/* Trust Ratings */}
                <motion.div 
                  className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-start sm:items-center w-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm font-semibold text-gray-700">4.9 / 5 on G2</span>
                    <img src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" alt="G2 Logo" className="h-4 w-4 sm:h-5 sm:w-5" width={20} height={20} />
                  </div>
                  <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <span className="text-sm font-semibold text-gray-500">3000+ reviews</span>
                  </a>
                </motion.div>
              </div>
              
              {/* Right Section: Announcement Preview Image */}
              <div className="relative w-full h-full flex items-center justify-center lg:col-span-3 mt-6 lg:mt-0">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="relative"
                >
                  <img 
                    src={announcementsHero} 
                    alt="Announcements interface showing company update with read tracking and engagement metrics"
                    className="w-full h-auto rounded-xl shadow-2xl"
                  />
                </motion.div>
              </div>
            </div>
            
            {/* Trust Section at Bottom */}
            <motion.div 
              className="text-center pt-8 lg:pt-12 mt-8 space-y-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            >
              <p className="text-lg text-gray-600">
                The #1 Internal Communications Platform - Trusted by Leading Teams Worldwide
              </p>
              <HeroBadgesSection />
              <div className="pt-4">
                <TrustLogosSection />
              </div>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Complete Announcements Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Everything you need to create, deliver, and track effective company communications
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.features.map((item) => (
                    <li key={item} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Announcement Types Showcase */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Announcements for Every Situation
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From company-wide updates to urgent communications, deliver the right message at the right time
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Globe className="h-6 w-6" />,
                title: "Company-Wide Updates",
                description: "Share important news and organizational announcements",
                color: "from-blue-500 to-cyan-500",
                bgColor: "bg-blue-50"
              },
              {
                icon: <Users className="h-6 w-6" />,
                title: "Department-Specific",
                description: "Target announcements to specific teams",
                color: "from-green-500 to-teal-500",
                bgColor: "bg-green-50"
              },
              {
                icon: <Calendar className="h-6 w-6" />,
                title: "Scheduled Posts",
                description: "Plan announcements for optimal timing",
                color: "from-purple-500 to-pink-500",
                bgColor: "bg-purple-50"
              },
              {
                icon: <Bell className="h-6 w-6" />,
                title: "Urgent Alerts",
                description: "Immediate notifications for critical updates",
                color: "from-orange-500 to-red-500",
                bgColor: "bg-orange-50"
              }
            ].map((type, index) => (
              <motion.div 
                key={type.title}
                className={`${type.bgColor} rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${type.color} rounded-full flex items-center justify-center mx-auto mb-6 text-white`}>
                  {type.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{type.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {type.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              The Impact of Effective Announcements
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Organizations using Assembly's announcements platform see measurable improvements in communication
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300"
              >
                <div className="w-12 h-12 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-blue-100 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about Announcements
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">How do announcements reach employees?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Announcements are delivered through multiple channels: they appear in the main feed, can trigger email notifications, send mobile push alerts, and integrate with Slack channels. This multi-channel approach ensures critical messages reach everyone.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">Can I track who has seen my announcements?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Yes! Our "Mark as Seen" feature lets you track exactly who has viewed your announcements. You can see read rates, send reminders to those who haven't seen it, and view detailed engagement analytics.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">Can I target announcements to specific groups?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Absolutely! You can target announcements by department, role, location, or create custom groups. This ensures relevant information reaches the right people without overwhelming everyone with irrelevant updates.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">Can I schedule announcements for later?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Yes! You can schedule announcements to publish at a future date and time, perfect for coordinating with events or ensuring messages go out during optimal hours. You can also set duration controls to automatically end announcements.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">Who can create announcements?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                You have complete control over permissions. You can allow admins only, all managers, or create custom permission groups based on departments, roles, or locations. This flexibility ensures the right people can communicate effectively.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold">What types of content can I include?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Announcements support rich media including images, videos, links, and formatted text. You can control whether recipients can comment or react, making each announcement as interactive or formal as needed.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
    </ProductPageLayout>
  );
};

export default Announcements;